// frontend/src/modules/client/AuthModule.jsx
// VERSION AVEC SCANNER QR CODE INTÉGRÉ (caméra smartphone)
// 73+ Papy - Décembre 2025

import React, { useState, useRef, useEffect } from 'react';
import { Leaf, LogIn, Shield, Mail, QrCode, Camera, X, AlertCircle } from 'lucide-react';
import LegalModal from '../../components/LegalModal';
import CookieBanner from '../../components/CookieBanner';

// Configuration API
const API_BASE = process.env.REACT_APP_API_URL || '';

// Fonction pour obtenir l'icône du réseau social
const getReseauIcon = (type) => {
  const icons = {
    'facebook': '📘',
    'instagram': '📷',
    'twitter': '🐦',
    'youtube': '📺',
    'linkedin': '🔗',
    'tiktok': '🎵',
    'pinterest': '📌',
    'snapchat': '👻',
    'whatsapp': '💬',
    'telegram': '✈️'
  };
  return icons[type] || '🌐';
};

const AuthModule = ({ onAuth, onAdminLogin, config }) => {
  // ==========================================
  // ÉTATS PRINCIPAUX
  // ==========================================
  const [authMode, setAuthMode] = useState('email');
  const [qrCode, setQrCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminLogin, setAdminLogin] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  
  // États pour les modals légales
  const [legalModal, setLegalModal] = useState({ isOpen: false, type: null });

  // ==========================================
  // ÉTATS SCANNER QR CODE
  // ==========================================
  const [showScanner, setShowScanner] = useState(false);
  const [scannerError, setScannerError] = useState('');
  const [hasCamera, setHasCamera] = useState(true);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const scanIntervalRef = useRef(null);

  const estFerme = config?.ferme_temporairement === 'oui';
  const dateDebut = config?.date_fermeture_debut;
  const dateFin = config?.date_fermeture_fin;
  const messageFermeture = config?.message_fermeture || 'Nous sommes temporairement fermés.';

  // ==========================================
  // SCANNER QR CODE - Fonctions
  // ==========================================
  
  // Démarrer le scanner
  const startScanner = async () => {
    setScannerError('');
    setShowScanner(true);

    try {
      // Demander accès à la caméra arrière en priorité
      const constraints = {
        video: {
          facingMode: { ideal: 'environment' }, // Caméra arrière
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        
        // Démarrer la détection après que la vidéo soit prête
        videoRef.current.onloadedmetadata = () => {
          startQRDetection();
        };
      }
    } catch (err) {
      console.error('Erreur accès caméra:', err);
      if (err.name === 'NotAllowedError') {
        setScannerError('Accès à la caméra refusé. Veuillez autoriser l\'accès dans les paramètres de votre navigateur.');
      } else if (err.name === 'NotFoundError') {
        setScannerError('Aucune caméra détectée sur cet appareil.');
        setHasCamera(false);
      } else {
        setScannerError('Impossible d\'accéder à la caméra: ' + err.message);
      }
    }
  };

  // Arrêter le scanner
  const stopScanner = () => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setShowScanner(false);
    setScannerError('');
  };

  // Détection QR code via canvas
  const startQRDetection = () => {
    // Charger dynamiquement jsQR si pas déjà chargé
    if (!window.jsQR) {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.min.js';
      script.onload = () => {
        beginScanning();
      };
      script.onerror = () => {
        setScannerError('Erreur chargement du module de scan');
      };
      document.head.appendChild(script);
    } else {
      beginScanning();
    }
  };

  const beginScanning = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    if (!video || !canvas || !window.jsQR) return;

    const ctx = canvas.getContext('2d');

    scanIntervalRef.current = setInterval(() => {
      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = window.jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: 'dontInvert'
        });

        if (code) {
          console.log('QR Code détecté:', code.data);
          handleQRCodeDetected(code.data);
        }
      }
    }, 200); // Scan toutes les 200ms
  };

  // QR Code détecté
  const handleQRCodeDetected = (data) => {
    stopScanner();

    // Vérifier si c'est une URL de connexion directe
    // Format attendu: https://lafermedupetitessart.fr/login/CLI-XXX/QR-XXX
    if (data.includes('/login/')) {
      // Redirection directe vers l'URL
      window.location.href = data;
    } else if (data.startsWith('QR-') || data.startsWith('CLI-')) {
      // C'est juste le code QR brut, on le met dans le champ
      setQrCode(data);
      setAuthMode('qrcode');
    } else if (data.includes('farm-booking:')) {
      // Format token complet: farm-booking:CLI-XXX:QR-XXX
      const parts = data.split(':');
      if (parts.length === 3) {
        // Construire l'URL et rediriger
        const url = `${window.location.origin}/login/${parts[1]}/${parts[2]}`;
        window.location.href = url;
      }
    } else {
      // Code inconnu, on le met dans le champ
      setQrCode(data);
      setAuthMode('qrcode');
    }
  };

  // Cleanup au démontage
  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  // ==========================================
  // GESTION AUTHENTIFICATION QR CODE
  // ==========================================
  const handleQrCodeSubmit = async (e) => {
    e.preventDefault();
    if (!qrCode.trim()) {
      setError('Veuillez scanner votre QR code');
      return;
    }

    setLoading(true);
    setError('');

    const result = await onAuth(qrCode.trim(), 'qrcode');

    if (result.success) {
      setQrCode('');
    } else {
      setError(result.error || 'Code QR non reconnu');
    }

    setLoading(false);
  };

  // ==========================================
  // GESTION AUTHENTIFICATION EMAIL + MOT DE PASSE
  // ==========================================
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emailTrimmed = email.trim().toLowerCase();
    
    if (!emailTrimmed) {
      setError('Veuillez saisir votre adresse email');
      return;
    }
    if (!emailRegex.test(emailTrimmed)) {
      setError('Veuillez saisir une adresse email valide');
      return;
    }
    if (!password) {
      setError('Veuillez saisir votre mot de passe');
      return;
    }

    setLoading(true);
    setError('');

    // ✅ CORRIGÉ: onAuth attend (identifier, type, password) séparément
    const result = await onAuth(emailTrimmed, 'email', password);

    if (result.success) {
      setEmail('');
      setPassword('');
    } else {
      setError(result.error || 'Email ou mot de passe incorrect');
    }

    setLoading(false);
  };

  
  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    
    if (!adminLogin.trim()) {
      setError('Veuillez saisir votre identifiant');
      return;
    }
    if (!adminPassword) {
      setError('Veuillez saisir votre mot de passe');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/auth/admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          login: adminLogin.trim(),
          password: adminPassword
        })
      });

      const result = await response.json();

      if (result.success) {
        localStorage.setItem('farm_admin_token', result.token);
        localStorage.setItem('farm_admin_user', JSON.stringify(result.user));
        onAdminLogin(true);
      } else {
        setError(result.error || 'Identifiants incorrects');
        setAdminPassword('');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  
  const openLegalModal = (type) => {
    setLegalModal({ isOpen: true, type });
  };

  const closeLegalModal = () => {
    setLegalModal({ isOpen: false, type: null });
  };

  const getLegalContent = () => {
    switch (legalModal.type) {
      case 'cgv':
        return {
          title: 'Conditions Générales de Vente',
          content: config?.cgv || ''
        };
      case 'rgpd':
        return {
          title: 'Politique de Confidentialité (RGPD)',
          content: config?.politique_rgpd || ''
        };
      case 'retractation':
        return {
          title: 'Conditions de Rétractation',
          content: config?.conditions_retractation || ''
        };
      default:
        return { title: '', content: '' };
    }
  };
 
  const LegalFooter = () => {
    return (
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="flex flex-wrap justify-center gap-3 text-xs text-gray-500">
          <button
            onClick={() => openLegalModal('cgv')}
            className="hover:text-green-600 hover:underline transition-colors"
          >
            CGV
          </button>
          <span>•</span>
          <button
            onClick={() => openLegalModal('rgpd')}
            className="hover:text-green-600 hover:underline transition-colors"
          >
            RGPD
          </button>
          <span>•</span>
          <button
            onClick={() => openLegalModal('retractation')}
            className="hover:text-green-600 hover:underline transition-colors"
          >
            Conditions de rétractation
          </button>
        </div>
      </div>
    );
  };

  // Sélecteur de mode
  const AuthModeSelector = () => {
    return (
      <div className="flex gap-2 mb-6">
        <button
          type="button"
          onClick={() => {
            setAuthMode('email');
            setError('');
            setQrCode('');
          }}
          className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
            authMode === 'email'
              ? 'bg-green-600 text-white shadow-lg'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Mail className="inline h-5 w-5 mr-2" />
          Email
        </button>
        <button
          type="button"
          onClick={() => {
            setAuthMode('qrcode');
            setError('');
            setEmail('');
            setPassword('');
       }}
          className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
            authMode === 'qrcode'
              ? 'bg-green-600 text-white shadow-lg'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <QrCode className="inline h-5 w-5 mr-2" />
          QR Code
        </button>
      </div>
    );
  };

 
  const ScannerModal = () => {
    if (!showScanner) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex flex-col">
         
        <div className="bg-black p-4 flex items-center justify-between">
          <h3 className="text-white font-semibold text-lg">📷 Scanner QR Code</h3>
          <button
            onClick={stopScanner}
            className="text-white p-2 hover:bg-gray-800 rounded-full"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Zone vidéo */}
        <div className="flex-1 flex items-center justify-center relative">
          {scannerError ? (
            <div className="text-center p-6">
              <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <p className="text-white text-lg mb-4">{scannerError}</p>
              <button
                onClick={stopScanner}
                className="bg-white text-black px-6 py-2 rounded-lg font-medium"
              >
                Fermer
              </button>
            </div>
          ) : (
            <>
              <video
                ref={videoRef}
                className="max-w-full max-h-full"
                playsInline
                muted
              />
              {/* Cadre de visée */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-64 h-64 border-4 border-green-500 rounded-2xl relative">
                  <div className="absolute -top-1 -left-1 w-8 h-8 border-t-4 border-l-4 border-green-400 rounded-tl-xl"></div>
                  <div className="absolute -top-1 -right-1 w-8 h-8 border-t-4 border-r-4 border-green-400 rounded-tr-xl"></div>
                  <div className="absolute -bottom-1 -left-1 w-8 h-8 border-b-4 border-l-4 border-green-400 rounded-bl-xl"></div>
                  <div className="absolute -bottom-1 -right-1 w-8 h-8 border-b-4 border-r-4 border-green-400 rounded-br-xl"></div>
                </div>
              </div>
              {/* Ligne de scan animée */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-64 h-64 overflow-hidden">
                  <div className="w-full h-1 bg-green-500 animate-pulse shadow-lg shadow-green-500/50"></div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Instructions */}
        <div className="bg-black p-4 text-center">
          <p className="text-gray-300 text-sm">
            Placez le QR code de votre badge dans le cadre
          </p>
        </div>

        {/* Canvas caché pour l'analyse */}
        <canvas ref={canvasRef} className="hidden" />
      </div>
    );
  };

  if (showAdminLogin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <Shield className="h-16 w-16 mx-auto text-gray-600 mb-4" />
            <h1 className="text-2xl font-bold text-gray-900">Administration</h1>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <form onSubmit={handleAdminSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Identifiant
                </label>
                <input
                  type="text"
                  value={adminLogin}
                  onChange={(e) => {
                    setAdminLogin(e.target.value);
                    setError('');
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                  placeholder="Votre identifiant"
                  autoFocus
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mot de passe
                </label>
                <input
                  type="password"
                  value={adminPassword}
                  onChange={(e) => {
                    setAdminPassword(e.target.value);
                    setError('');
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                  placeholder="••••••••"
                  disabled={loading}
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gray-800 text-white py-3 rounded-lg hover:bg-gray-900 transition-colors font-medium disabled:opacity-50"
              >
                {loading ? 'Connexion...' : 'Se connecter'}
              </button>
            </form>

            <button
              onClick={() => {
                setShowAdminLogin(false);
                setError('');
                setAdminLogin('');
                setAdminPassword('');
              }}
              className="w-full mt-4 text-gray-500 hover:text-gray-700 text-sm"
            >
              ← Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ==========================================
  // VUE FERMETURE TEMPORAIRE
  // ==========================================
  if (estFerme) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-yellow-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="text-6xl mb-4">🚜</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {config?.nom_ferme || 'La Ferme du Petit Essart'}
          </h1>
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
            <p className="text-lg text-gray-700 mb-4">{messageFermeture}</p>
            {dateDebut && dateFin && (
              <p className="text-sm text-gray-500">
                Fermeture du {new Date(dateDebut).toLocaleDateString('fr-FR')} au{' '}
                {new Date(dateFin).toLocaleDateString('fr-FR')}
              </p>
            )}
          </div>
          <button
            onClick={() => setShowAdminLogin(true)}
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            <Shield className="inline h-4 w-4 mr-1" />
            Accès administrateur
          </button>
        </div>
      </div>
    );
  }

  // ==========================================
  // VUE PRINCIPALE
  // ==========================================
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex items-center justify-center p-4">
      <ScannerModal />

      <div className="max-w-md w-full">
        {/* En-tête */}
        <div className="text-center mb-8">
          <img 
            src={config?.logo_url || '/logo.png'}
            alt="Logo"
            className="h-24 w-24 mx-auto mb-4 object-contain rounded-full shadow-lg"
            onError={(e) => {
              e.target.style.display = 'none';
              document.getElementById('fallback-leaf')?.style.setProperty('display', 'block');
            }}
          />
          <Leaf 
            id="fallback-leaf"
            className="h-24 w-24 mx-auto text-green-600 mb-4 drop-shadow-lg" 
            style={{ display: 'none' }}
          />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {config?.nom_ferme || 'La Ferme du Petit Essart'}
          </h1>
          <p className="text-gray-600">
            Réservez vos produits frais de la ferme
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="flex items-center justify-center mb-6">
            <LogIn className="h-8 w-8 text-green-600 mr-2" />
            <h2 className="text-2xl font-bold text-gray-900">Connexion</h2>
          </div>

          <AuthModeSelector />

          {/* FORMULAIRE EMAIL + MOT DE PASSE */}
          {authMode === 'email' && (
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError('');
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="votre.email@exemple.fr"
                  autoFocus
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mot de passe
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError('');
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Votre mot de passe"
                  disabled={loading}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Le mot de passe reçu dans votre email de bienvenue
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50"
              >
                {loading ? 'Vérification...' : 'Se connecter'}
              </button>
            </form>
          )}

          {/* FORMULAIRE QR CODE */}
          {authMode === 'qrcode' && (
            <div className="space-y-4">
              {/* Bouton Scanner Caméra */}
              {hasCamera && (
                <button
                  type="button"
                  onClick={startScanner}
                  className="w-full bg-blue-600 text-white py-4 rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-3"
                >
                  <Camera className="h-6 w-6" />
                  <span className="text-lg">📷 Scanner mon badge</span>
                </button>
              )}

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">ou saisir le code</span>
                </div>
              </div>

              {/* Saisie manuelle */}
              <form onSubmit={handleQrCodeSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code QR
                  </label>
                  <input
                    type="text"
                    value={qrCode}
                    onChange={(e) => {
                      setQrCode(e.target.value);
                      setError('');
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent font-mono"
                    placeholder="QR-XXXXXXXXXX"
                    disabled={loading}
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !qrCode.trim()}
                  className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50"
                >
                  {loading ? 'Vérification...' : 'Se connecter'}
                </button>
              </form>
            </div>
          )}

          {/* Informations pratiques */}
          <div className="mt-6 pt-6 border-t">
            <div className="text-center space-y-1 text-sm text-gray-600">
              {config?.adresse && <p>📍 {config.adresse}</p>}
              {config?.telephone && <p>📞 {config.telephone}</p>}
              {config?.email_contact && <p>📧 {config.email_contact}</p>}
            </div>
          </div>

          <LegalFooter />
        </div>

        <div className="text-center mt-6">
          <button
           // onClick={() => setShowAdminLogin(true)}
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            <Shield className="inline h-4 w-4 mr-1" />
             
          </button>
        </div>
      </div>

      <LegalModal 
        isOpen={legalModal.isOpen}
        onClose={closeLegalModal}
        {...getLegalContent()}
        config={config}
      />
      <CookieBanner />
    </div>
  );
};

export default AuthModule;